import React, { useEffect, useState } from 'react';
import NPCDialogueText from './NPCDialogueText';
import NPCDialogueEditor from './NPCDialogueEditor';

interface NPCDialogProps {
  npcId: string;
  currentMessage: string;
  messageIndex: number;
  totalMessages: number;
  messages?: string[];
  canEdit?: boolean;
  onSave?: (messages: string[]) => Promise<void>;
  onNext: () => void;
  onClose: () => void;
}

const NPCDialog: React.FC<NPCDialogProps> = ({
  npcId,
  currentMessage,
  messageIndex,
  totalMessages,
  messages = [],
  canEdit = false,
  onSave,
  onNext,
  onClose,
}) => {
  const hasMoreMessages = messageIndex < totalMessages - 1;
  const [showEditor, setShowEditor] = useState(false);
  const [draftMessages, setDraftMessages] = useState<string[]>(messages);
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState<{ error: boolean; text: string } | null>(null);

  useEffect(() => {
    setDraftMessages(messages);
    setShowEditor(false);
    setSaveMessage(null);
  }, [npcId, messages]);

  const save = async () => {
    if (!onSave) return;
    const cleaned = draftMessages.map((message) => message.trim()).filter(Boolean);
    if (cleaned.length === 0) {
      setSaveMessage({ error: true, text: 'Add at least one message.' });
      return;
    }
    setSaving(true);
    setSaveMessage(null);
    try {
      await onSave(cleaned);
      setDraftMessages(cleaned);
      setSaveMessage({ error: false, text: 'Dialogue saved.' });
    } catch (error) {
      setSaveMessage({ error: true, text: error instanceof Error ? error.message : 'Could not save dialogue.' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div 
      className="unity-modal-overlay absolute inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50"
      onClick={onClose}
      style={{ zIndex: 10003 }}
    >
      <div 
        className="relative mx-4 max-h-[90vh] max-w-xl overflow-y-auto rounded-lg border-2 border-cyan-500 bg-gray-900 p-6 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-2 right-2 w-8 h-8 bg-red-600 text-white rounded-full hover:bg-red-700 transition-colors flex items-center justify-center text-lg font-bold"
        >
          ×
        </button>

        {/* NPC Header */}
        <div className="mb-4">
          <h3 className="text-xl font-bold text-cyan-400">
            Intersect Staff Member
          </h3>
        </div>

        {/* Message */}
        <div className="bg-gray-800 border border-gray-700 rounded-lg p-4 mb-4">
          <div className="text-lg">
            <NPCDialogueText>{currentMessage}</NPCDialogueText>
          </div>
        </div>

        {/* Progress indicator */}
        {totalMessages > 1 && (
          <div className="flex items-center justify-center gap-2 mb-4">
            {Array.from({ length: totalMessages }).map((_, index) => (
              <div
                key={index}
                className={`h-2 rounded-full transition-all ${
                  index === messageIndex
                    ? 'w-8 bg-cyan-500'
                    : index < messageIndex
                    ? 'w-2 bg-cyan-700'
                    : 'w-2 bg-gray-600'
                }`}
              />
            ))}
          </div>
        )}

        {/* Action buttons */}
        <div className="flex gap-3 justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors"
          >
            Dismiss
          </button>
          {hasMoreMessages && (
            <button
              onClick={onNext}
              className="px-6 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 transition-colors font-semibold"
            >
              Continue →
            </button>
          )}
        </div>

        {canEdit && (
          <div className="mt-4 border-t border-cyan-900/70 pt-4">
            <button
              type="button"
              onClick={() => setShowEditor((open) => !open)}
              className="text-sm font-semibold text-cyan-300 hover:text-cyan-200"
              aria-expanded={showEditor}
            >
              {showEditor ? 'Hide dialogue editor' : 'Edit this NPC’s dialogue'}
            </button>
            {showEditor && (
              <div className="mt-3 space-y-2 rounded-lg border border-gray-700 bg-gray-950/60 p-3">
                <p className="text-xs text-gray-400">Each entry is shown as one step in this conversation.</p>
                {draftMessages.map((message, index) => (
                  <div key={index} className="flex items-start gap-2">
                    <NPCDialogueEditor
                      value={message}
                      messageNumber={index + 1}
                      disabled={saving}
                      onChange={(value) => setDraftMessages((current) => current.map((message, i) => i === index ? value : message))}
                    />
                    {draftMessages.length > 1 && (
                      <button
                        type="button"
                        onClick={() => setDraftMessages((current) => current.filter((_, i) => i !== index))}
                        className="rounded-md bg-red-950 px-2 py-1.5 text-xs text-red-200 hover:bg-red-900"
                        aria-label={`Remove message ${index + 1}`}
                      >
                        Remove
                      </button>
                    )}
                  </div>
                ))}
                <div className="flex flex-wrap items-center justify-between gap-2 pt-1">
                  <button
                    type="button"
                    disabled={draftMessages.length >= 20}
                    onClick={() => setDraftMessages((current) => [...current, ''])}
                    className="rounded-md bg-gray-700 px-3 py-1.5 text-xs font-semibold text-white hover:bg-gray-600 disabled:opacity-40"
                  >
                    Add message
                  </button>
                  <button
                    type="button"
                    disabled={saving}
                    onClick={() => void save()}
                    className="rounded-md bg-cyan-600 px-4 py-1.5 text-sm font-semibold text-white hover:bg-cyan-500 disabled:opacity-50"
                  >
                    {saving ? 'Saving…' : 'Save dialogue'}
                  </button>
                </div>
                {saveMessage && (
                  <p className={`text-xs ${saveMessage.error ? 'text-red-300' : 'text-green-300'}`}>
                    {saveMessage.text}
                  </p>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default NPCDialog;
